import React from 'react';
import { fireEvent, render, screen, waitFor } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import BulkCreateUserWizard from './index';
import {
  createBulkUserImport,
  fetchBulkUserImport,
} from '../../services/bulkUserImportService';

jest.mock('../../services/bulkUserImportService', () => ({
  createBulkUserImport: jest.fn(),
  fetchBulkUserImport: jest.fn(),
  TEMPLATE_PATH: '/management/bulk_user_imports/template.csv',
}));

const onClose = jest.fn();
const onCompleted = jest.fn();

const renderWizard = () =>
  render(
    <ConfigProvider>
      <BulkCreateUserWizard
        visible
        onClose={onClose}
        onCompleted={onCompleted}
      />
    </ConfigProvider>,
  );

const csv = (overrides = {}) =>
  new File(['Email*\njane@example.com\n'], 'users.csv', {
    type: 'text/csv',
    ...overrides,
  });

const choose = (file) => {
  const input = screen.getByTestId('bulk-user-import-file-input');
  Object.defineProperty(input, 'files', { value: [file], configurable: true });
  fireEvent.change(input);
};

const continueButton = () => screen.getByRole('button', { name: /continue/i });

const running = { id: 3, status: 'processing', isComplete: false };

// The wizard polls on its real 2s interval, which outlasts both waitFor's 1s
// default and Jest's own 5s per-test limit. Fake timers are not an option here:
// React 16's async flush rides on setImmediate, which Jest also fakes.
const POLL_TIMEOUT = { timeout: 6000 };

jest.setTimeout(20000);

beforeEach(() => {
  onClose.mockClear();
  onCompleted.mockClear();
  createBulkUserImport.mockReset();
  fetchBulkUserImport.mockReset();
  createBulkUserImport.mockResolvedValue(running);
  fetchBulkUserImport.mockResolvedValue(running);
});

describe('management/Users BulkCreateUserWizard', () => {
  it('opens on the upload step with both of the mockup s actions', () => {
    renderWizard();

    expect(screen.getByText('Create Multiple Users')).toBeInTheDocument();
    expect(screen.getByText('List of Users')).toBeInTheDocument();
    expect(screen.getByText('UPLOAD CSV FILE')).toBeInTheDocument();
    expect(screen.getByText('DOWNLOAD CSV TEMPLATE')).toBeInTheDocument();
  });

  // The template and the report are both real downloads, so both have to render
  // as anchors that actually point somewhere — a styled button that swallows
  // href would look identical on screen and do nothing.
  it('points the template link at the template endpoint', () => {
    renderWizard();

    expect(
      screen.getByText('DOWNLOAD CSV TEMPLATE').closest('a'),
    ).toHaveAttribute('href', '/management/bulk_user_imports/template.csv');
  });

  // The two controls whose behavior *is* the browser's default action, checked
  // the only way jsdom can check them: it implements neither the file picker nor
  // navigation, so all that can be asserted is that the click reaches them
  // uncancelled. KeyDS's modal calls preventDefault on every click in its body,
  // which silently disabled both until modalClick.js was added — the button did
  // nothing at all, with no console error to say why.
  const clickAndReportCancellation = (element) => {
    const event = new MouseEvent('click', { bubbles: true, cancelable: true });
    element.dispatchEvent(event);
    return event.defaultPrevented;
  };

  it('opens the file picker rather than having its click cancelled', () => {
    renderWizard();

    const input = screen.getByTestId('bulk-user-import-file-input');
    let cancelled = null;
    input.click = () => {
      cancelled = clickAndReportCancellation(input);
    };

    fireEvent.click(screen.getByText('UPLOAD CSV FILE').closest('button'));

    expect(cancelled).toBe(false);
  });

  it('lets the template download rather than having its click cancelled', () => {
    renderWizard();

    expect(
      clickAndReportCancellation(
        screen.getByText('DOWNLOAD CSV TEMPLATE').closest('a'),
      ),
    ).toBe(false);
  });

  it('states both of the design s limits', () => {
    renderWizard();

    expect(
      screen.getByText(
        'Max file size 10 MB. and/or 40,000 Users. One User per row required.',
      ),
    ).toBeInTheDocument();
  });

  it('cannot continue until a file is chosen', () => {
    renderWizard();

    expect(continueButton()).toBeDisabled();
  });

  it('names the chosen file and enables continue', async () => {
    renderWizard();

    choose(csv());

    await waitFor(() =>
      expect(screen.getByText('users.csv')).toBeInTheDocument(),
    );
    expect(continueButton()).not.toBeDisabled();
  });

  // Checked in the browser because the design shows the answer against the
  // field the moment a file is picked — a 10 MB round trip to be told it is
  // 10 MB is what that avoids.
  it('refuses a file of the wrong type without uploading it', async () => {
    renderWizard();

    choose(new File(['x'], 'users.pdf', { type: 'application/pdf' }));

    await waitFor(() =>
      expect(
        screen.getByText('Invalid file type. Please upload a csv file.'),
      ).toBeInTheDocument(),
    );
    expect(continueButton()).toBeDisabled();
    expect(createBulkUserImport).not.toHaveBeenCalled();
  });

  it('refuses a file over the size limit without uploading it', async () => {
    renderWizard();

    const big = csv();
    Object.defineProperty(big, 'size', { value: 11 * 1024 * 1024 });
    choose(big);

    await waitFor(() =>
      expect(
        screen.getByText('File exceeds the maximum size of 10 MB'),
      ).toBeInTheDocument(),
    );
    expect(createBulkUserImport).not.toHaveBeenCalled();
  });

  it('lets the admin remove a chosen file and start again', async () => {
    renderWizard();

    choose(csv());
    await waitFor(() =>
      expect(screen.getByText('users.csv')).toBeInTheDocument(),
    );

    fireEvent.click(screen.getByRole('button', { name: 'Remove file' }));

    await waitFor(() =>
      expect(screen.queryByText('users.csv')).not.toBeInTheDocument(),
    );
    expect(screen.getByText('UPLOAD CSV FILE')).toBeInTheDocument();
  });

  describe('once the upload is accepted', () => {
    const uploadFile = async () => {
      renderWizard();
      choose(csv());
      await waitFor(() => expect(continueButton()).not.toBeDisabled());
      fireEvent.click(continueButton());
    };

    it('shows the processing panel and promises the run survives closing', async () => {
      await uploadFile();

      await waitFor(() =>
        expect(screen.getByText('Creating Users...')).toBeInTheDocument(),
      );
      expect(
        screen.getByText(/Closing this window won't interrupt it/),
      ).toBeInTheDocument();
    });

    // The design puts CLOSE beside the disabled progress button precisely so the
    // admin can walk away; the page behind keeps watching (see useUserEvents).
    it('offers CLOSE while it runs, and closing does not report a completion', async () => {
      await uploadFile();

      await waitFor(() =>
        expect(screen.getByText('Creating Users...')).toBeInTheDocument(),
      );

      fireEvent.click(screen.getByRole('button', { name: 'CLOSE' }));

      expect(onClose).toHaveBeenCalled();
      expect(onCompleted).not.toHaveBeenCalled();
    });

    it('shows the success summary when every row landed', async () => {
      fetchBulkUserImport.mockResolvedValue({
        id: 3,
        status: 'success',
        isComplete: true,
        succeeded: 90,
        failed: 0,
        total: 90,
        reportPath: '/management/bulk_user_imports/3/download.csv',
      });

      await uploadFile();

      await waitFor(
        () =>
          expect(
            screen.getByText(
              '90 Users have been created and are ready to use for 90 Accounts.',
            ),
          ).toBeInTheDocument(),
        POLL_TIMEOUT,
      );
      const link = screen.getByText('DOWNLOAD REPORT').closest('a');
      expect(link).toHaveAttribute(
        'href',
        '/management/bulk_user_imports/3/download.csv',
      );
      expect(link).toHaveAttribute('download');
      // Pointing somewhere is not enough inside a KeyDS modal — see the two
      // cancellation tests above.
      expect(clickAndReportCancellation(link)).toBe(false);
    });

    it('shows both numbers when only some rows landed', async () => {
      fetchBulkUserImport.mockResolvedValue({
        id: 3,
        status: 'partial_success',
        isComplete: true,
        succeeded: 98,
        failed: 4,
        total: 102,
        reportPath: '/management/bulk_user_imports/3/download.csv',
      });

      await uploadFile();

      await waitFor(
        () =>
          expect(
            screen.getByText('98 Users successfully created.'),
          ).toBeInTheDocument(),
        POLL_TIMEOUT,
      );
      expect(
        screen.getByText('4 Users failed to be created.'),
      ).toBeInTheDocument();
    });

    it('shows the failure summary when nothing landed', async () => {
      fetchBulkUserImport.mockResolvedValue({
        id: 3,
        status: 'failure',
        isComplete: true,
        succeeded: 0,
        failed: 12,
        total: 12,
        reportPath: '/management/bulk_user_imports/3/download.csv',
      });

      await uploadFile();

      await waitFor(
        () =>
          expect(
            screen.getByText(
              'All 12 Users failed to be created to the Accounts.',
            ),
          ).toBeInTheDocument(),
        POLL_TIMEOUT,
      );
    });

    // A file nothing could be read from has no count to quote, so it says so
    // rather than claiming "All 0 Users failed".
    it('says so plainly when no rows could be read at all', async () => {
      fetchBulkUserImport.mockResolvedValue({
        id: 3,
        status: 'failure',
        isComplete: true,
        succeeded: 0,
        failed: 0,
        total: 0,
      });

      await uploadFile();

      await waitFor(
        () =>
          expect(
            screen.getByText('No users could be read from the uploaded file.'),
          ).toBeInTheDocument(),
        POLL_TIMEOUT,
      );
    });

    it('reports the completion when the admin closes from the summary', async () => {
      fetchBulkUserImport.mockResolvedValue({
        id: 3,
        status: 'success',
        isComplete: true,
        succeeded: 1,
        failed: 0,
        total: 1,
        reportPath: '/management/bulk_user_imports/3/download.csv',
      });

      await uploadFile();
      // The summary panel's own text, not the heading: "Summary" is also the
      // step tracker's label for step 2, which is on screen from the start.
      await waitFor(
        () =>
          expect(
            screen.getByText(
              '1 Users have been created and are ready to use for 1 Accounts.',
            ),
          ).toBeInTheDocument(),
        POLL_TIMEOUT,
      );

      fireEvent.click(screen.getByRole('button', { name: 'CLOSE' }));

      expect(onCompleted).toHaveBeenCalled();
      expect(onClose).toHaveBeenCalled();
    });

    it("shows the server's reason when it rejects the file", async () => {
      const error = new Error('unprocessable');
      error.fieldErrors = { original_file: ['must be smaller than 10 MB'] };
      createBulkUserImport.mockRejectedValue(error);

      await uploadFile();

      await waitFor(() =>
        expect(
          screen.getByText('must be smaller than 10 MB'),
        ).toBeInTheDocument(),
      );
      expect(screen.queryByText('Creating Users...')).not.toBeInTheDocument();
    });
  });
});
